// §7C-HR — SERVER-BACKED creator app source of truth. Hits the gateway
// (same-origin, vs_session cookie) which lists/resolves owned apps from D1
// app_metadata by the authenticated session — so a fresh browser / new device /
// cleared cache still sees owned apps. localStorage recent-apps is a cache only.
import type { RecentApp } from './recent-apps';

export async function fetchServerApps(): Promise<RecentApp[]> {
  try {
    const r = await fetch('/v1/creator/apps', { credentials: 'include' });
    if (!r.ok) return [];
    const j = (await r.json()) as { apps?: RecentApp[] };
    return Array.isArray(j.apps) ? j.apps : [];
  } catch { return []; }
}

/** Resolve one owned app from the server. status 404 = missing OR not owned by
 *  the signed-in creator (no existence leak) → App Detail shows not-found. */
export interface LaneConsole { id: string; label: string; statusField?: string; category: string; customerWritten: boolean; content?: { fields: import('@vibeshell/shared').PublicSubmitField[] } }
export interface LaneDescriptor { primary: string | null; ownerConsoles: LaneConsole[] }

export interface DeliveryRecord { state: 'built_to_try' | 'ready' | 'unknown'; mandatoryTotal: number; mandatoryMet: number; unmet: { id: string; text: string; status: 'UNMET' | 'UNVERIFIED' }[] }

export async function fetchServerApp(appId: string): Promise<{ app: RecentApp | null; lanes?: LaneDescriptor; delivery?: DeliveryRecord; status: number }> {
  try {
    const r = await fetch(`/v1/creator/apps/${encodeURIComponent(appId)}`, { credentials: 'include' });
    if (!r.ok) return { app: null, status: r.status };
    // §CANONICAL-LANE-DESCRIPTOR — `lanes` is the server's authoritative lane set (primary AND every
    // composed secondary, at their real namespaced collection ids). The Owner Workspace mounts from
    // it instead of regexing the app's name+prompt, which only ever found the primary lane.
    const j = (await r.json()) as { app?: RecentApp; lanes?: LaneDescriptor; delivery?: DeliveryRecord };
    return { app: j.app ?? null, lanes: j.lanes, delivery: j.delivery, status: 200 };
  } catch { return { app: null, status: 0 }; }
}

/** Delete one owned app on the server (DELETE /v1/apps/:appId, vs_session cookie).
 *  The gateway is authoritative: it deletes ONLY when the signed-in creator owns
 *  the app (D1 app_metadata.accountId), returns 404 for a foreign/missing app
 *  (no existence leak) and is idempotent on a repeat delete. Returns `ok` true
 *  ONLY for a 2xx — the caller must not remove the card until then. */
export async function deleteServerApp(appId: string): Promise<{ ok: boolean; status: number }> {
  try {
    const r = await fetch(`/v1/apps/${encodeURIComponent(appId)}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    return { ok: r.ok, status: r.status };
  } catch { return { ok: false, status: 0 }; }
}
